/**
 * PQL tokenizer. Every token carries [start, end) character offsets so parse
 * errors can point at the exact spot in the statement.
 */

import { PqlError } from './types.ts';

export type TokenType =
  | 'ident' // fields, keywords, bareword values (incl. dotted meta.key)
  | 'string' // "..." or '...'
  | 'number'
  | 'date' // YYYY-MM-DD
  | 'duration' // -30d / -12w / -6M / -1y (relative to now)
  | 'op' // = != ~ !~ > >= < <=
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'dotdot' // .. (bounded range literal: 2..5, 2026-01-01..2026-03-31)
  | 'eof';

export interface Token {
  type: TokenType;
  /** Raw source text (strings are unquoted/unescaped in `value`). */
  text: string;
  /** Parsed value for string tokens; equals `text` otherwise. */
  value: string;
  start: number;
  end: number;
}

const OPERATORS = ['!=', '!~', '>=', '<=', '=', '~', '>', '<'] as const;
const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const DURATION_RE = /^-\d+(?:\.\d+)?[dwMy]\b/;
const NUMBER_RE = /^-?\d+(?:\.\d+)?/;
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)?/;

export function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;

  const push = (type: TokenType, text: string, value = text) => {
    tokens.push({ type, text, value, start: pos, end: pos + text.length });
    pos += text.length;
  };

  while (pos < input.length) {
    const ch = input[pos];
    const rest = input.slice(pos);

    if (/\s/.test(ch)) {
      pos++;
      continue;
    }
    if (ch === '(') { push('lparen', ch); continue; }
    if (ch === ')') { push('rparen', ch); continue; }
    if (ch === ',') { push('comma', ch); continue; }
    if (rest.startsWith('..')) { push('dotdot', '..'); continue; }

    if (ch === '"' || ch === "'") {
      let i = pos + 1;
      let value = '';
      let closed = false;
      while (i < input.length) {
        const c = input[i];
        if (c === '\\' && i + 1 < input.length) {
          value += input[i + 1];
          i += 2;
          continue;
        }
        if (c === ch) {
          closed = true;
          i++;
          break;
        }
        value += c;
        i++;
      }
      if (!closed) throw new PqlError('Unclosed quote', pos, input.length);
      const text = input.slice(pos, i);
      tokens.push({ type: 'string', text, value, start: pos, end: i });
      pos = i;
      continue;
    }

    const opMatch = OPERATORS.find((o) => rest.startsWith(o));
    // `-30d` must win over reading `-` as a failed operator / negative number sign.
    const durMatch = DURATION_RE.exec(rest);
    if (durMatch) { push('duration', durMatch[0]); continue; }
    if (opMatch) { push('op', opMatch); continue; }

    const dateMatch = DATE_RE.exec(rest);
    if (dateMatch) { push('date', dateMatch[0]); continue; }

    const numMatch = NUMBER_RE.exec(rest);
    if (numMatch) { push('number', numMatch[0]); continue; }

    const identMatch = IDENT_RE.exec(rest);
    if (identMatch) { push('ident', identMatch[0]); continue; }

    throw new PqlError(`Unexpected character "${ch}"`, pos, pos + 1);
  }

  tokens.push({ type: 'eof', text: '', value: '', start: pos, end: pos });
  return tokens;
}
